import profileService from '../../../services/profileService';
import filterService from '../../../services/filterService';

import {
    setProfileLoading,
    setProfileError,
    setProfileList,
    setProfileCurrent,
    setProfilePagination,
    setProfileSort,
    setProfileFilters,
    setProfileDefaultFilter,
    setProfileSettings,
    clearProfileCurrent,
    clearProfileError,
    setProfileFilterLoading,
    changeProfileFilterCondition,
    addProfileFilterCondition,
    updateProfileFilterConditionRule,
    updateProfileFilterConditionValue,
    removeProfileFilterCondition,
    setProfileSelected,
    setProfileDownloading,
    resetProfileFiltersAndPagination,

    setProfileClassicFilters
} from './profileSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().profile.isSettingsInitialized) {
            dispatch(setProfileLoading(true));
            const response = await profileService.getSettings();

            // Set the settings
            dispatch(setProfileSettings(response));

            const classicFilters = response?.classic_filters || [];
            dispatch(setProfileClassicFilters(classicFilters));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setProfileDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setProfileError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setProfileLoading(false));
    }
};

export const fetchProfiles = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().profile;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setProfileLoading(true));
        const response = await profileService.getList(finalQueryParams, finalActiveFilter, state.classicFilters);
        dispatch(setProfileList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchProfiles:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setProfileError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setProfileLoading(false));
    }
};

export const fetchProfile = (id) => async (dispatch) => {
    try {
        dispatch(setProfileLoading(true));
        const response = await profileService.getOne(id);
        dispatch(setProfileCurrent(response.data));
    } catch (error) {
        dispatch(setProfileError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setProfileLoading(false));
    }
};

export const createProfile = (data) => async (dispatch) => {
    try {
        const response = await profileService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateProfile = (id, data) => async (dispatch) => {
    try {
        const response = await profileService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteProfile = (id) => async (dispatch) => {
    try {
        dispatch(setProfileLoading(true));
        const response = await profileService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setProfileError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setProfileLoading(false));
    }
};

export const downloadProfile = (type) => async (dispatch, getState) => {
    try {
        const state = getState().profile;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setProfileDownloading(true));
        const response = await profileService.download(finalQueryParams, filter, state.classicFilters);
        dispatch(setProfileDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading profiles:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setProfileError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setProfileDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setProfilePagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setProfileSort(sort));
};

export const searchProfiles = (query) => (dispatch, getState) => {
    const currentFilters = getState().profile.filters;
    dispatch(setProfileFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setProfileDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeProfileFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addProfileFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateProfileFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateProfileFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeProfileFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setProfileFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().profile.settings;
            dispatch(setProfileSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setProfileFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setProfileFilterLoading(false));
        dispatch(setProfileError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setProfileSelected(selectedRows));
    };
};

export const clearCurrentProfile = () => {
    return (dispatch) => {
        dispatch(clearProfileCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetProfileFiltersAndPagination());
};

export const changeClassicFilter = (name, value) => (dispatch, getState) => {
    const currentClassicFilters = getState().profile.classicFilters;

    // Create a deep copy of the classic filters
    const updatedClassicFilters = { ...currentClassicFilters };
    
    // Update the nested value in the query object
    if (updatedClassicFilters[name]) {
        updatedClassicFilters[name] = {
            ...updatedClassicFilters[name],
            query: {
                ...updatedClassicFilters[name].query,
                ...(typeof value === 'object' && value !== null ? value : { value })
            }
        };
    }

    dispatch(setProfileClassicFilters(updatedClassicFilters));
};